import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, Tabs, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import request from '../utils/request';
import ImageUpload from '../components/ImageUpload';

const typeOptions = [
  { label: '门诊介绍', value: 'CLINIC' },
  { label: '荣誉介绍', value: 'HONOR' },
];

const Contents: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [type, setType] = useState('CLINIC');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  // 新增/编辑在途锁（Modal 的 onOk 不感知 promise）+ 行内启停在途的行 ID
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetchList = async (p = page, t = type) => {
    setLoading(true);
    try {
      const res: any = await request.get('/content', { params: { page: p, pageSize: 10, type: t } });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page, type]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (saving) return;
    setSaving(true);
    try {
      values.type = type;
      if (editItem) {
        await request.put(`/content/${editItem.id}`, values);
        message.success('更新成功');
      } else {
        await request.post('/content', values);
        message.success('创建成功');
      }
      setModalOpen(false);
      form.resetFields();
      setEditItem(null);
      fetchList();
    } finally {
      setSaving(false);
    }
  };

  // 行内启停：一次只允许一处在途，其余按钮置灰，避免连点把状态翻回原值
  const toggleStatus = async (record: any) => {
    if (busyId !== null) return;
    setBusyId(record.id);
    try {
      await request.put(`/content/${record.id}`, { status: record.status === 1 ? 0 : 1 });
      fetchList();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除？',
      onOk: async () => {
        await request.delete(`/content/${id}`);
        message.success('删除成功');
        fetchList();
      },
    });
  };

  const openEdit = (record: any) => {
    setEditItem(record);
    form.setFieldsValue({ ...record, images: record.images || [] });
    setModalOpen(true);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '标题', dataIndex: 'title' },
    { title: '类型', dataIndex: 'type', width: 100, render: (v: string) => v === 'CLINIC' ? <Tag color="green">门诊介绍</Tag> : <Tag color="gold">荣誉介绍</Tag> },
    { title: '图片数', width: 80, render: (_: any, r: any) => (r.images || []).length },
    { title: '排序', dataIndex: 'sort', width: 80 },
    { title: '状态', dataIndex: 'status', width: 80, render: (v: number) => v === 1 ? <Tag color="success">启用</Tag> : <Tag>禁用</Tag> },
    {
      title: '操作', width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>编辑</Button>
          <Button
            size="small"
            loading={busyId === record.id}
            disabled={busyId !== null && busyId !== record.id}
            onClick={() => toggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>内容管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditItem(null); form.resetFields(); setModalOpen(true); }}>
          新增内容
        </Button>
      </div>
      <Tabs
        activeKey={type}
        onChange={(key) => { setType(key); setPage(1); }}
        items={typeOptions.map((t) => ({ key: t.value, label: t.label }))}
      />
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />
      <Modal title={editItem ? '编辑内容' : '新增内容'} open={modalOpen} width={640}
        onOk={handleSubmit} onCancel={() => !saving && setModalOpen(false)}
        okButtonProps={{ loading: saving }}>
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="images" label="图片列表">
            <ImageUpload multiple tip="宽度自适应展示，建议宽度不小于 750px，单张不超过 5MB" />
          </Form.Item>
          <Form.Item name="body" label="详情介绍">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="sort" label="排序" initialValue={0}>
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Contents;
